import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import generatePDF, { Resolution } from 'react-to-pdf';
import useStudy from '../hooks/useStudy';
import CircularProgressLoader from '@Components/CircularProgressLoader';
import verticeRedSvg from '../components/VerticeLogo/vertice_red_logo.svg';
import {} from '@mui/x-data-grid';
import StudySummaryTable from '@Components/Tables/StudySummaryTable';
import StudyRecordsTable from '@Components/Tables/StudyRecordsTable';
const excludedSummaryColumns = ['uuid', 'api', 'status', 'lease', 'county'];
export default function StudySummaryPage() {
    const { uuid } = useParams();
    const { getStudy } = useStudy();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const targetRef = useRef(null);
    function formatCellValue(fieldName, rawValue) {
        if (!rawValue) {
            return '';
        }
        const fieldsToRoundToTenths = ['irr', 'p50_frac_npv', 'eur_delta', 'frac_lookback_cost', 'moic'];
        const fieldsToRoundToWholeNumbers = ['lateral_length', 'stage_spacing', 'frac_proppant', 'payback', 'nearest_offset'];
        let value = String(rawValue);
        value = value.replace(/\$/g, '').replace(/%/g, '');
        value = value.trim();
        let parsed = parseFloat(value);
        if (isNaN(parsed)) {
            return rawValue;
        }
        if (fieldsToRoundToTenths.includes(fieldName)) {
            parsed = parseFloat(parsed.toFixed(1));
        }
        else if (fieldsToRoundToWholeNumbers.includes(fieldName)) {
            parsed = parseFloat(parsed.toFixed(0));
        }
        let finalString = String(parsed);
        return finalString;
    }
    function getStatusOrderIndex(status) {
        const statusOrder = ['success', 'failed', 'in_progress', 'queued'];
        return statusOrder.indexOf(status);
    }
    async function fetchData() {
        setLoading(true);
        try {
            const response = await getStudy(uuid);
            response.records.sort((a, b) => {
                return getStatusOrderIndex(a.status) - getStatusOrderIndex(b.status);
            });
            setData(response);
        }
        catch (err) {
            setError('An unexpected error occurred');
        }
        finally {
            setLoading(false);
        }
    }
    const handleRowClick = (params) => {
        const clickedStatus = params.row.status;
        const clickedApi = params.row.api;
        if (clickedStatus === 'success') {
            navigate(`/studies/${uuid}/api/${clickedApi}`);
        }
    };
    async function handleGeneratePDF() {
        if (!targetRef.current) {
            return;
        }
        const logoContainer = document.createElement('div');
        logoContainer.style.display = 'flex';
        logoContainer.style.justifyContent = 'flex-start';
        const logoEl = document.createElement('img');
        logoEl.src = verticeRedSvg;
        logoEl.style.width = '362px';
        logoEl.style.height = '256px';
        logoContainer.appendChild(logoEl);
        targetRef.current.insertBefore(logoContainer, targetRef.current.firstChild);
        const options = {
            method: 'save',
            filename: 'well-study.pdf',
            resolution: Resolution.MEDIUM,
            page: {
                format: 'a4',
                orientation: 'landscape',
                margin: 5
            },
            canvas: {
                mimeType: 'image/jpeg',
                qualityRatio: 0.9
            },
            overrides: {
                canvas: {
                    windowWidth: 2400
                }
            }
        };
        generatePDF(targetRef, options);
        logoContainer.remove();
    }
    useEffect(() => {
        fetchData();
    }, [uuid]);
    useEffect(() => {
        if (data && data.records.some((r) => r.status === 'queued' || r.status === 'in_progress')) {
            const timer = setTimeout(fetchData, 5000);
            return () => clearTimeout(timer);
        }
    }, [data]);
    if (loading) {
        return _jsx(CircularProgressLoader, {});
    }
    if (error) {
        return (_jsx(Box, { textAlign: "center", mt: 5, children: _jsx(Typography, { variant: "h6", color: "error", children: error }) }));
    }
    if (!data) {
        return (_jsx(Box, { textAlign: "center", mt: 5, children: _jsx(Typography, { variant: "h6", children: "No data available" }) }));
    }
    function getColumnWidth(id) {
        if (id === 'irr' || id === 'moic') {
            return 110;
        }
        return 120;
    }
    const stringFields = ['api', 'status', 'lease', 'county', 'stage_spacing'];
    const columns = data.headers
        .filter((header) => header.id !== 'uuid')
        .map((header) => ({
        field: header.id,
        headerName: header.label,
        width: getColumnWidth(header.id),
        renderHeader: () => _jsx("strong", { children: header.label }),
        sortable: true,
        headerClassName: '.MuiDataGrid-columnHeaders',
        type: stringFields.includes(header.id) ? 'string' : 'number',
        valueFormatter: (params) => {
            if (!params) {
                return '';
            }
            return formatCellValue(header.id, String(params));
        }
    }));
    const rows = data.records.map(function (record) {
        const formattedRecord = { id: record.uuid };
        Object.entries(record).forEach(function ([key, value]) {
            if (record.status === 'failed' && key !== 'status' && key !== 'api' && key !== 'lease' && key !== 'county') {
                formattedRecord[key] = '';
            }
            else if (key !== 'uuid') {
                formattedRecord[key] = formatCellValue(key, value);
            }
        });
        return formattedRecord;
    });
    const summaryColumns = [
        {
            field: 'metric',
            headerName: 'Metric',
            width: 485,
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => _jsx("strong", { children: "Metric" }),
            type: 'string',
        },
        ...data.headers
            .filter((header) => {
            if (['Status', 'Lease', 'County'].includes(header.label))
                return false;
            if (header.id === 'uuid' || header.id === 'api')
                return false;
            return true;
        })
            .map((header) => ({
            field: header.id,
            headerName: header.label,
            width: getColumnWidth(header.id),
            sortable: true,
            renderHeader: () => _jsx("strong", { children: header.label }),
            type: 'number',
            valueFormatter: (params) => {
                if (!params) {
                    return '';
                }
                return formatCellValue(header.id, String(params));
            }
        }))
    ];
    const summaryRows = ['min', 'median', 'average', 'max'].map((key) => {
        const rowData = {
            id: key,
            metric: key.charAt(0).toUpperCase() + key.slice(1)
        };
        data.headers.forEach((header) => {
            if (['Status', 'Lease', 'County'].includes(header.label) ||
                header.id === 'uuid' ||
                header.id === 'api') {
                return;
            }
            if (excludedSummaryColumns.includes(header.id)) {
                rowData[header.id] = '';
            }
            else if (key === 'failed') {
                rowData[header.id] = '';
            }
            else {
                const val = data.summary[key]?.[header.id];
                rowData[header.id] = val ? formatCellValue(header.id, val) : '';
            }
        });
        return rowData;
    });
    return (_jsxs(Box, { textAlign: "center", mt: 5, children: [_jsx(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '24px'
                }, children: _jsx(Typography, { variant: "h4", children: data.studyName }) }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start', marginBottom: 2 }, children: _jsx(Button, { variant: "contained", onClick: handleGeneratePDF, children: "Download PDF Report" }) }), _jsxs("div", { ref: targetRef, children: [_jsx(Box, { sx: { backgroundColor: 'white', borderRadius: '20px', marginBottom: 4 }, children: data.records.length > 0 ? (_jsx(StudyRecordsTable, { rows: rows, columns: columns, onRowClick: handleRowClick })) : (_jsx(Typography, { variant: "body2", children: "No well analysis records found" })) }), data.records.length > 0 && (_jsx(Box, { sx: { backgroundColor: 'white', borderRadius: '20px', marginTop: 2 }, children: _jsx(StudySummaryTable, { rows: summaryRows, columns: summaryColumns }) }))] })] }));
}
